import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { selectStatsData } from '../../redux/statsSlice';
import { Box, Container, Typography } from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon, Flag as FlagIcon, DirectionsCar as DirectionsCarIcon,
  People as PeopleIcon
} from '@mui/icons-material';

function AnimatedSection({ children, delay = 0 }) {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay },
      });
    }
  }, [controls, inView, delay]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={controls}
      style={{ width: '100%' }}
    >
      {children}
    </motion.div>
  );
}

const Statistics = () => {
  const data = useSelector(selectStatsData);
  const stats = [
    {
      icon: <DirectionsCarIcon />,
      value: String(data?.competitors ?? 0),
      label: 'Competitors',
      color: '#0f172a',
    },
    {
      icon: <PeopleIcon />,
      value: String(data?.countries ?? 0),
      label: 'Countries',
      color: '#0f172a',
    },
    {
      icon: <EmojiEventsIcon />,
      value: String(data?.events ?? 0),
      label: 'Events',
      color: '#0f172a',
    },
    {
      icon: <FlagIcon />,
      value: String(data?.champions ?? 0),
      label: 'Champions',
      color: '#0f172a',
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: '#ffffff',
        py: { xs: 12, md: 18 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decoration Text */}
      <Typography
        variant="h1"
        sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: { xs: '8rem', md: '18rem' },
          fontWeight: 900,
          color: 'transparent',
          WebkitTextStroke: '1px rgba(15, 23, 42, 0.1)',
          userSelect: 'none',
          zIndex: 0,
          whiteSpace: 'nowrap',
          fontFamily: 'Bebas Neue'
        }}
      >
        STATS
      </Typography>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ mb: { xs: 8, md: 12 }, textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{
              color: '#c2410c',
              fontWeight: 700,
              letterSpacing: '3px',
              mb: 1,
              display: 'block'
            }}
          >
            Our Performance
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Bebas Neue',
              mb: 3,
              color: '#0f172a',
              fontSize: { xs: '3.5rem', md: '5rem' },
              letterSpacing: '2px',
              textAlign: 'center'
            }}
          >
            Championship <Box component="span" sx={{ color: '#c2410c' }}>Statistics</Box>
          </Typography>
          <Box
            sx={{
              width: '80px',
              height: '4px',
              bgcolor: '#c2410c',
              margin: '0 auto 24px',
              borderRadius: '2px'
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              color: '#64748b',
              maxWidth: '650px',
              margin: '0 auto',
              fontSize: '1.25rem',
              lineHeight: 1.6
            }}
          >
            Behind every roar of the engine is a story written in numbers. Explore the scale of our global championship legacy.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 5 }}>
          {stats.map((stat, index) => (
            <Box key={index} sx={{ display: 'flex', width: { xs: "45%", sm: "30%", md: '20%' }, maxWidth: "250px" }}>
              <AnimatedSection delay={index * 0.1}>
                <Box
                  sx={{
                    position: 'relative',
                    p: 4,
                    pt: 8,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    bgcolor: '#ffffff',
                    borderRadius: '40px 4px 40px 4px',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    boxShadow: '0 15px 45px rgba(15, 23, 42, 0.05)',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 30px 60px rgba(194, 65, 12, 0.15)',
                      borderColor: '#c2410c',
                      '& .icon-container': {
                        transform: 'scale(1.1) rotate(10deg)',
                        bgcolor: '#c2410c',
                        color: '#ffffff',
                      },
                      '& .stat-value': {
                        color: '#c2410c',
                        transform: 'skewX(-10deg) scale(1.05)',
                      }
                    },
                  }}
                >
                  {/* Floating Icon */}
                  <Box
                    className="icon-container"
                    sx={{
                      position: 'absolute',
                      top: -25,
                      right: 20,
                      width: 70,
                      height: 70,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '18px',
                      bgcolor: '#ffffff',
                      color: '#c2410c',
                      boxShadow: '0 10px 25px rgba(194, 65, 12, 0.2)',
                      border: '2px solid #c2410c',
                      transition: 'all 0.5s ease',
                      zIndex: 2,
                      '& svg': { fontSize: '2rem' }
                    }}
                  >
                    {stat.icon}
                  </Box>

                  <Typography
                    className="stat-value"
                    variant="h3"
                    sx={{
                      mb: 0.5,
                      fontFamily: 'Bebas Neue',
                      fontSize: { xs: '4rem', md: '5rem' },
                      color: '#0f172a',
                      letterSpacing: '1px',
                      transform: 'skewX(-6deg)',
                      transition: 'all 0.4s ease',
                      lineHeight: 1
                    }}
                  >
                    {stat.value}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'Bebas Neue',
                      color: '#64748b',
                      fontSize: '1.4rem',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      opacity: 0.8
                    }}
                  >
                    {stat.label}
                  </Typography>

                  {/* Technical Accent Line */}
                  <Box
                    sx={{
                      width: '40px',
                      height: '2px',
                      bgcolor: '#c2410c',
                      mt: 3,
                      borderRadius: '1px'
                    }}
                  />
                </Box>
              </AnimatedSection>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Statistics;
